import { getAttributeConditionTypesByLevel, getAttributesForLevel, getOperatorsForAttribute } from 'reducers/rule/utils.ts';
import { AttributeMetadata } from 'schemas/metadata/attributes.ts';
import { OperatorMetadata } from 'schemas/metadata/operators.ts';
import { CohortMetadata } from 'schemas/metadata/cohorts.ts';

export interface IRuleState {
    selectedAttributeLevel: string;
    selectedConditionType: string;
    selectedAttribute: AttributeMetadata;
    selectedOperator: OperatorMetadata;
    selectedComparator: string;
    availableLevels: string[];
    availableConditionTypes: string[];
    availableAttributes: AttributeMetadata[];
    availableOperators: OperatorMetadata[];
    name: string;
    description: string;
    cohortLabel?: string;
}

export interface IRuleAttributeLevel {
  selectedAttributeLevel : string;
  defaultConditionType: string;
}

type SetAttributeLevelAction = {
  type: 'SET_ATTRIBUTE_LEVEL',
  payload: IRuleAttributeLevel
}

type SetConditionTypeAction = {
    type: 'SET_CONDITION_TYPE',
    payload: string
}

type SetAttributeAction = {
    type: 'SET_ATTRIBUTE',
    payload: string
}

type SetOperatorAction = {
    type: 'SET_OPERATOR',
    payload: string
}

type SetComparatorAction = {
    type: 'SET_COMPARATOR',
    payload: string
}

type SetNameAction = {
    type: 'SET_NAME',
    payload: string
}

type SetDescriptionAction = {
    type: 'SET_DESCRIPTION',
    payload: string
}

type SetCohortLabelAction = {
    type: 'SET_COHORT_LABEL',
    payload: string | undefined
}

type SetInitialStateAction = {
    type: 'SET_INITIAL_STATE',
    payload: IRuleState
}

export type RuleActions = SetAttributeLevelAction |
    SetConditionTypeAction |
    SetAttributeAction |
    SetOperatorAction |
    SetComparatorAction |
    SetNameAction |
    SetDescriptionAction |
    SetInitialStateAction |
    SetCohortLabelAction;

const findSelectedAttribute = (state: IRuleState) => ({ AttributeName }: AttributeMetadata) => AttributeName === state.selectedAttribute.AttributeName;
const findSelectedOperator = (state: IRuleState) => ({ Operator }: OperatorMetadata) => Operator === state.selectedOperator?.Operator;

export const ruleReducer = (
  state: IRuleState,
  action: RuleActions,
  attributes: AttributeMetadata[],
  operators: OperatorMetadata[],
  cohorts: CohortMetadata[],
): IRuleState => {
  switch (action.type) {
    case 'SET_ATTRIBUTE_LEVEL': {
      if (action.payload.selectedAttributeLevel === 'COHORT') {
        return {
          ...state,
          selectedAttributeLevel: 'COHORT',
          selectedConditionType: 'ALL',
          selectedOperator: operators.find((operator) => operator.Operator === 'MemberOf') || operators[0],
          selectedAttribute: attributes.find((attribute) => attribute.AttributeLevel === 'COHORT') || attributes[attributes.length - 1],
          availableAttributes: attributes.filter((attribute) => attribute.AttributeLevel === 'COHORT'),
          availableOperators: operators.filter((operator) => ['MemberOf', 'NotMemberOf'].includes(operator.Operator)),
          availableConditionTypes: ['ALL'],
          selectedComparator: cohorts[0].CohortLabel,
        };
      }

      const newSelectedAttributeLevel = action.payload.selectedAttributeLevel;
      const newConditionTypes = getAttributeConditionTypesByLevel(attributes, action.payload.selectedAttributeLevel);
      let newSelectedConditionType = action.payload.selectedAttributeLevel !== 'TARGET' ? newConditionTypes[0] : action.payload.defaultConditionType;

      if (newSelectedConditionType.includes('_')) {
        const allConditions = newSelectedConditionType.split('_');
        newSelectedConditionType = allConditions[0] as string;
      }

      const newAvailableAttributes = getAttributesForLevel(attributes, newSelectedAttributeLevel, newSelectedConditionType);
      const newSelectedAttribute = newAvailableAttributes[0];
      const newAvailableOperators = getOperatorsForAttribute(operators, newSelectedAttribute);
      const newSelectedOperator = newAvailableOperators[0];

      return {
        ...state,
        selectedAttributeLevel: newSelectedAttributeLevel,
        selectedConditionType: newSelectedConditionType,
        selectedOperator: newSelectedOperator,
        selectedAttribute: newSelectedAttribute,
        availableAttributes: newAvailableAttributes,
        availableOperators: newAvailableOperators,
        availableConditionTypes: newConditionTypes,
        selectedComparator: '',
      };
    }
    case 'SET_CONDITION_TYPE': {
      const newAvailableAttributes = getAttributesForLevel(attributes, state.selectedAttributeLevel, action.payload);

      if (newAvailableAttributes.length === 0) {
        return {
          ...state,
          selectedConditionType: action.payload,
          selectedAttribute: {
            ...state.selectedAttribute,
            ConditionType: action.payload,
          },
        };
      }

      const newSelectedAttribute = newAvailableAttributes[newAvailableAttributes.findIndex(findSelectedAttribute(state)) ?? 0];
      const newAvailableOperators = getOperatorsForAttribute(operators, newSelectedAttribute);
      const operatorIndex = newAvailableOperators.findIndex(findSelectedOperator(state));
      const newSelectedOperator = operatorIndex >= 0
        ? newAvailableOperators[operatorIndex]
        : newAvailableOperators[0];

      return {
        ...state,
        selectedConditionType: action.payload,
        selectedOperator: newSelectedOperator,
        selectedAttribute: newSelectedAttribute,
        availableAttributes: newAvailableAttributes,
        availableOperators: newAvailableOperators,
      };
    }
    case 'SET_ATTRIBUTE': {
      const selectedAttribute = state.availableAttributes.find((attribute) => attribute.AttributeName === action.payload);

      if (!selectedAttribute) {
        return { ...state };
      }

      const newAvailableOperators = getOperatorsForAttribute(operators, selectedAttribute);

      return {
        ...state,
        selectedAttribute: state.availableAttributes.find((attr) => attr.AttributeName === action.payload) || state.availableAttributes[0],
        selectedOperator: newAvailableOperators[0],
        availableOperators: newAvailableOperators,
      };
    }
    case 'SET_OPERATOR':
      return {
        ...state,
        selectedOperator: state.availableOperators.find((operator) => operator.Operator === action.payload) || state.availableOperators[0],
      };
    case 'SET_COMPARATOR':
      return {
        ...state,
        selectedComparator: action.payload,
      };
    case 'SET_NAME':
      return {
        ...state,
        name: action.payload,
      };
    case 'SET_DESCRIPTION':
      return {
        ...state,
        description: action.payload,
      };
    case 'SET_COHORT_LABEL':
      return {
        ...state,
        cohortLabel: action.payload,
      };
    case 'SET_INITIAL_STATE':
      return {
        ...action.payload,
      };
    default:
      return { ...state };
  }
};

export default ruleReducer;
